import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { FiCpu, FiZap, FiSend, FiLoader, FiCheck, FiTrendingUp } from 'react-icons/fi';
import { supabase } from '@/integrations/supabase/client';

// ---------------------------------------------------------------------
// AiQuotaWidget — user-facing card on AccountBilling. Shows:
//   • which AI model the user is currently routed to (per-user override
//     → plan mapping → global default), with the resolved primary/fallback
//     from ai_plan_mapping when relevant
//   • their spend this month vs the plan cap
//   • a Claude access request button when they're on GPT and no request
//     is already pending
//   • a top-up placeholder button (phase 2: Stripe metered billing) for
//     users who want to keep hitting the flagship model past the cap
//
// Read-only from the user's perspective — none of the buttons mutate
// admin_settings; they only touch the user's own profile via RPCs.
// ---------------------------------------------------------------------

interface QuotaState {
  provider: string;
  model: string;
  monthCost: number;
  planCap: number | null;
  fallbackModel: string | null;
  hasPendingRequest: boolean;
  requestDecision: string | null;
  planId: string | null;
  topupCredit: number;
}

// Top-up ladder — 4 packs. Higher packs = better $/credit ratio to
// nudge users toward larger buys. Values MUST match the ALLOWED_AMOUNTS
// set in supabase/functions/create-topup-checkout/index.ts.
const TOPUP_PACKS: Array<{ amount: number; label: string; hint?: string }> = [
  { amount: 10,  label: '$10' },
  { amount: 25,  label: '$25', hint: 'popular' },
  { amount: 50,  label: '$50', hint: 'best value' },
  { amount: 100, label: '$100' },
];

const AiQuotaWidget: React.FC = () => {
  const [state, setState] = useState<QuotaState | null>(null);
  const [loading, setLoading] = useState(true);
  const [requesting, setRequesting] = useState(false);
  const [requestError, setRequestError] = useState<string | null>(null);
  const [requestNote, setRequestNote] = useState('');
  const [buyingTopup, setBuyingTopup] = useState<number | null>(null);
  const [topupError, setTopupError] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: profile } = await supabase
        .from('user_profiles')
        .select('ai_provider, ai_model, subscription_plan_id, ai_access_requested_at, ai_access_decision')
        .eq('user_id', user.id)
        .maybeSingle();

      const [costRes, planRes, creditRes] = await Promise.all([
        supabase.rpc('get_user_month_cost', { p_user_id: user.id }),
        profile?.subscription_plan_id
          ? supabase.rpc('get_ai_plan_mapping', { p_plan_id: profile.subscription_plan_id })
          : Promise.resolve({ data: null }),
        supabase.rpc('get_user_topup_credit', { p_user_id: user.id }),
      ]);

      const planMap: any = Array.isArray(planRes.data) ? planRes.data[0] : planRes.data;

      const resolvedProvider = profile?.ai_provider || planMap?.provider || 'openai';
      const resolvedModel = profile?.ai_model || planMap?.model || 'gpt-4o';

      setState({
        provider: resolvedProvider,
        model: resolvedModel,
        monthCost: Number(costRes.data || 0),
        planCap: planMap?.monthly_cap_usd ? Number(planMap.monthly_cap_usd) : null,
        fallbackModel: planMap?.fallback_model || null,
        hasPendingRequest: !!profile?.ai_access_requested_at && !profile?.ai_access_decision,
        requestDecision: profile?.ai_access_decision || null,
        planId: profile?.subscription_plan_id || null,
        topupCredit: Number(creditRes.data || 0),
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const buyTopup = async (amount: number) => {
    setBuyingTopup(amount);
    setTopupError(null);
    try {
      const { data, error } = await supabase.functions.invoke('create-topup-checkout', {
        body: { amount },
      });
      if (error) throw error;
      if (data?.url) {
        window.location.href = data.url;
        return;
      }
      throw new Error('Missing checkout URL');
    } catch (e: any) {
      setTopupError(e?.message || 'Top-up failed');
      setBuyingTopup(null);
    }
  };

  const submitRequest = async () => {
    setRequesting(true);
    setRequestError(null);
    try {
      const { data } = await supabase.rpc('request_claude_access', { p_note: requestNote || null });
      if (data && !data.success) {
        setRequestError(data.message || 'Request failed');
      } else {
        setRequestNote('');
        await load();
      }
    } catch (e: any) {
      setRequestError(e?.message || 'Request failed');
    } finally {
      setRequesting(false);
    }
  };

  if (loading) {
    return (
      <div className="rounded-2xl border border-slate-800 bg-slate-900/50 p-6 flex items-center gap-2 text-slate-500">
        <FiLoader className="animate-spin" /> Loading AI usage…
      </div>
    );
  }
  if (!state) return null;

  const isAnthropic = state.provider === 'anthropic';
  const pct = state.planCap ? Math.min(100, (state.monthCost / state.planCap) * 100) : 0;
  const capExceeded = state.planCap !== null && state.monthCost >= state.planCap;

  return (
    <div className="rounded-2xl border border-slate-800 bg-slate-900/50 p-6 space-y-5">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-cyan-400 mb-1">
            ▸ AI · Usage this month
          </div>
          <h3 className="text-lg font-semibold text-white flex items-center gap-2">
            <FiCpu className={isAnthropic ? 'text-purple-400' : 'text-cyan-400'} />
            {isAnthropic ? 'Anthropic Claude' : 'OpenAI GPT'}
          </h3>
          <div className="text-sm text-slate-400 font-mono mt-1">
            Model: <span className={isAnthropic ? 'text-purple-300' : 'text-cyan-300'}>{state.model}</span>
          </div>
        </div>
        {capExceeded && state.fallbackModel && (
          <div className="text-xs px-3 py-1.5 rounded-full bg-yellow-500/15 border border-yellow-500/40 text-yellow-200 font-mono uppercase tracking-widest">
            ▸ Cap reached · using fallback
          </div>
        )}
      </div>

      {/* Cap progress bar */}
      {state.planCap !== null ? (
        <div>
          <div className="flex items-baseline justify-between text-sm mb-1.5">
            <span className="text-slate-300">
              <span className="font-mono text-white">${state.monthCost.toFixed(2)}</span>
              <span className="text-slate-500"> of </span>
              <span className="font-mono text-slate-300">${Number(state.planCap).toFixed(2)}</span>
              <span className="text-slate-500 ml-1">this month</span>
            </span>
            <span className="font-mono text-xs text-slate-500">{pct.toFixed(0)}%</span>
          </div>
          <div className="h-2 rounded-full bg-slate-800 overflow-hidden">
            <div
              className={`h-full transition-all ${
                capExceeded
                  ? 'bg-yellow-400'
                  : pct > 75
                    ? 'bg-purple-400'
                    : 'bg-cyan-400'
              }`}
              style={{ width: `${pct}%` }}
            />
          </div>
          {capExceeded && state.fallbackModel && (
            <p className="text-xs text-slate-400 mt-2">
              You've reached your monthly cap on <span className="font-mono text-purple-300">{state.model}</span>.
              Analyses this month now use <span className="font-mono text-cyan-300">{state.fallbackModel}</span> —
              still premium quality, at a fraction of the compute cost.
            </p>
          )}
          {!capExceeded && pct > 75 && (
            <p className="text-xs text-slate-400 mt-2">
              You're approaching your cap. Once reached, we switch you to <span className="font-mono text-cyan-300">{state.fallbackModel}</span> automatically —
              no interruption, no surprise charge.
            </p>
          )}
        </div>
      ) : (
        <div className="rounded-lg border border-slate-800 bg-slate-950/40 px-3 py-2 text-sm text-slate-400 font-mono">
          Spent <span className="text-white">${state.monthCost.toFixed(2)}</span> this month · unlimited usage on your plan.
        </div>
      )}

      {/* Claude access request — only shows when on OpenAI + no pending */}
      {!isAnthropic && !state.hasPendingRequest && (
        <div className="rounded-lg border border-purple-500/20 bg-purple-500/[0.04] p-4 space-y-3">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-purple-300 mb-1">
              ▸ Try Claude
            </div>
            <p className="text-sm text-slate-300">
              Want Claude Sonnet 5 instead of GPT? Analyses run through Anthropic's flagship
              — different reasoning style, often catches setups GPT misses.
            </p>
          </div>
          <textarea
            value={requestNote}
            onChange={(e) => setRequestNote(e.target.value)}
            placeholder="Optional — tell us why (VIP trader, A/B testing, prefer Claude output…)"
            rows={2}
            className="w-full px-3 py-2 bg-slate-950 border border-slate-800 rounded-lg text-white text-sm placeholder:text-slate-600 focus:outline-none focus:border-purple-500/60"
          />
          {requestError && (
            <div className="text-sm text-red-300">{requestError}</div>
          )}
          <button
            onClick={submitRequest}
            disabled={requesting}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r from-cyan-500 to-purple-500 text-white text-sm font-semibold shadow-[0_0_14px_rgba(168,85,247,0.25)] hover:shadow-[0_0_22px_rgba(168,85,247,0.4)] transition-shadow disabled:opacity-50"
          >
            {requesting ? <FiLoader className="w-4 h-4 animate-spin" /> : <FiSend className="w-4 h-4" />}
            Request Claude access
          </button>
        </div>
      )}

      {/* Pending state */}
      {state.hasPendingRequest && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="rounded-lg border border-yellow-500/25 bg-yellow-500/[0.04] px-4 py-3 text-sm text-yellow-200 flex items-center gap-2 font-mono">
          <FiLoader className="animate-spin" /> Your Claude access request is pending admin review.
        </motion.div>
      )}

      {/* Already granted / denied — informational */}
      {isAnthropic && state.requestDecision === 'granted' && (
        <div className="rounded-lg border border-green-500/25 bg-green-500/[0.04] px-4 py-3 text-sm text-green-200 flex items-center gap-2 font-mono">
          <FiCheck /> Claude access granted. Your analyses run on {state.model}.
        </div>
      )}

      {/* Existing top-up credit indicator */}
      {state.topupCredit > 0 && (
        <div className="rounded-lg border border-green-500/25 bg-green-500/[0.05] px-4 py-3 flex items-center gap-3 flex-wrap">
          <FiZap className="text-green-400 flex-shrink-0" />
          <div className="text-sm text-green-100 flex-1 min-w-0 font-mono">
            <span className="font-bold">${state.topupCredit.toFixed(2)}</span> in top-up credit — keeps you on
            <span className="text-white ml-1">{state.model}</span> past your plan cap.
          </div>
        </div>
      )}

      {/* Top-up packs — shown whenever the user is on a capped plan.
          Persuasive when they've hit the cap; convenient before that. */}
      {state.planCap !== null && (
        <div className="rounded-lg border border-cyan-500/20 bg-cyan-500/[0.04] p-4 space-y-3">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-cyan-300 flex items-center gap-2">
              <FiTrendingUp /> ▸ {capExceeded ? `Keep using ${state.model}` : `Extend your flagship credit`}
            </div>
            <p className="text-sm text-slate-300 mt-1">
              {capExceeded
                ? <>You've hit your monthly cap. Top up to stay on <span className="font-mono text-white">{state.model}</span> instead of the fallback.</>
                : <>Buy extra flagship-model credit — never runs out mid-month, rolls off at month-end.</>}
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {TOPUP_PACKS.map(pack => (
              <button
                key={pack.amount}
                onClick={() => buyTopup(pack.amount)}
                disabled={buyingTopup !== null}
                className={`relative flex flex-col items-center justify-center gap-1 px-3 py-3 rounded-lg border transition-all text-white text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed ${
                  pack.hint
                    ? 'border-cyan-400/60 bg-gradient-to-b from-cyan-500/15 to-purple-500/10 hover:border-cyan-300 shadow-[0_0_14px_rgba(34,211,238,0.15)]'
                    : 'border-slate-700 bg-slate-950/40 hover:border-cyan-500/40'
                }`}
              >
                {buyingTopup === pack.amount ? (
                  <FiLoader className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <span className="text-lg font-bold font-mono">{pack.label}</span>
                    {pack.hint && (
                      <span className="font-mono text-[9px] uppercase tracking-widest text-cyan-300">{pack.hint}</span>
                    )}
                  </>
                )}
              </button>
            ))}
          </div>
          {topupError && (
            <div className="text-xs text-red-300 font-mono">{topupError}</div>
          )}
          <p className="text-[11px] text-slate-500 font-mono">
            Secure checkout via Stripe · one-time payment · credit added instantly on success.
          </p>
        </div>
      )}
    </div>
  );
};

export default AiQuotaWidget;
